import * as React from 'react'
import styled from 'styled-components'

import { AutocompleteField, COLORS, FONTS, glyphs, Link } from '@ulule/owl-kit-components/next'
import { BaseAddress } from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'
import * as logger from '@owl-nest/logger'
import { Country } from '@owl-nest/config'

import * as mapbox from './mapbox'

type AutoCompleteControlProps = {
  countries: Country[]
  className?: string
  hidden?: boolean
  label?: string
  context?: string
  language: string
  countryFilter?: string[]
  placeholder?: string
  onManual?: (address: BaseAddress) => void
  onSelect: (
    address: BaseAddress & {
      latitude: number
      longitude: number
    },
  ) => void
  searchInputRef?: React.RefObject<HTMLInputElement>
}

export function AutoCompleteControl({
  countries,
  hidden,
  label,
  context,
  language,
  countryFilter,
  onManual,
  onSelect,
  placeholder,
  className,
  searchInputRef,
}: AutoCompleteControlProps): React.ReactElement<AutoCompleteControlProps> {
  return (
    <AutocompleteStyled
      hidden={hidden}
      ref={searchInputRef}
      className={className}
      translations={{
        clear: t('Clear'),
      }}
      label={label}
      context={context}
      placeholder={placeholder}
      fetch={(query: string) => fetchAddress(countries, query, language, countryFilter)}
      onResultSelect={({ value }: AddressResult) => onSelect(value.address)}
      renderResult={({ result }: { result: AddressResult }) => {
        const highlight = result.value.highlight
        const fine = highlight.address1
        const coarse =
          highlight.state.length === 0
            ? `${highlight.city}, ${highlight.country}`
            : `${highlight.city}, ${highlight.state}, ${highlight.country}`
        return (
          <>
            <MapIcon size={18} />
            <ResultFine dangerouslySetInnerHTML={{ __html: fine }} />
            <ResultCoarse dangerouslySetInnerHTML={{ __html: coarse }} />
          </>
        )
      }}
      renderResultExtra={({ close }) => {
        if (onManual === undefined) {
          return <></>
        }

        return (
          <ResultExtra>
            <Link
              onMouseDown={() => {
                onManual({
                  address1: '',
                  address2: '',
                  country: countryFilter && countryFilter.length > 0 ? countryFilter[0] : '',
                  city: '',
                  state: '',
                  postal_code: '',
                })
                close()
              }}
            >
              {t('Add address manually')}
            </Link>
          </ResultExtra>
        )
      }}
      renderNoResult={({ close }: { close: () => void }) => {
        return (
          <NoResult>
            <NoResultLabel>{t('No result')}</NoResultLabel>
            {onManual && (
              <Link
                onMouseDown={() => {
                  onManual({
                    address1: '',
                    address2: '',
                    country: countryFilter && countryFilter.length > 0 ? countryFilter[0] : '',
                    city: '',
                    state: '',
                    postal_code: '',
                  })
                  close()
                }}
              >
                {t('Add address manually')}
              </Link>
            )}
          </NoResult>
        )
      }}
    />
  )
}

type AddressResult = {
  label: string
  value: mapbox.Result
}

function getCountryName(countries: Country[], countryCode: string): string {
  const country = countries?.find((country) => country !== undefined && country.code === countryCode)
  return country?.label() ?? ''
}

async function fetchAddress(
  countries: Country[],
  query: string,
  language: string,
  countryFilter?: string[],
): Promise<AddressResult[]> {
  const addresses = await mapbox.find(query, language, countryFilter)
  return addresses
    .map(({ address, highlight }) => ({
      label: `${address.address1} ${address.address2} ${address.postal_code} ${address.city} ${
        address.state
      } ${getCountryName(countries, address.country)}`,
      value: { address, highlight },
    }))
    .slice(0, 4)
}

const AutocompleteStyled = styled(AutocompleteField)`
  margin: 5px 0px 20px;
  display: ${({ hidden }) => (hidden ? 'none' : 'flex')};
  flex-direction: column;
  position: relative;
  z-index: 1;
`

const ResultText = styled.span`
  font-family: ${FONTS.PRIMARY_FONT};
  font-size: 15px;
  line-height: 18px;
  margin-right: 10px;
`

const Result = styled(ResultText)`
  em {
    font-style: normal;
    font-weight: 700;
  }
`

const ResultFine = styled(Result)`
  color: ${COLORS.PRIMARY_GREY_900};
`

const ResultCoarse = styled(Result)`
  color: ${COLORS.GREY_SHADE_3};
`

const MapIcon = styled(glyphs.stroke.MapCursor)`
  color: ${COLORS.PRIMARY_GREY_500};
  margin-right: 10px;
`

const NoResult = styled.li`
  display: flex;
  flex-direction: column;
  padding: 19px;
`

const NoResultLabel = styled(ResultText)`
  color: ${COLORS.GREY_SHADE_3};
  margin-bottom: 12px;
`

const ResultExtra = styled(NoResult)`
  padding: 10px 19px;
`
